import React from "react";
// import ShowDisjonctionEliminationArguments from "./ShowDisjonctionEliminationArgumentsAndButtons";

// Sections des composants utilisés par le composant principal
// Separate keyboard component
const VirtualKeyboard = ({ onCharacterClick }) => {
  const characterSets = [
    ["~", "∧", "∨", "⊻", "⊃", "⊅", "≡", "↑", "↓"],
    ["p", "q", "r", "s"],
    ["(", ")"],
  ];

  return (
    <div className="rule-modal-all-button-hypothesis">
      {characterSets.map((set, setIndex) => (
        <ul key={setIndex}>
          {set.map((char) => (
            <li
              key={char}
              className="rule-modal-hypothesis-button-character selectable"
              onClick={() => onCharacterClick(char)}
            >
              {char}
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
};

const ExpectedArgumentDisplay = ({
  // className, pas besoin de classname vu que ce sera toujours .expected-argument
  content,
}) => {
  return <div className={"expected-argument"}>{content}</div>;
};

const SubmittedArgumentDisplay = ({
  className, // submitted-argument ou noArgumentYet-blinking ou inferenceLongStored ou noLongStoredArgumentYet-blinking
  // peut aussi accueillir infIts-color
  content,
  additionalContent,
}) => {
  return (
    <div className={className}>
      {content}
      {additionalContent}
    </div>
  );
};

// Main component
const ShowExpectedArguments = ({
  ruleName,
  expectedArguments,
  valueInference,
  // valueRule,
}) => {
  const { storedInference, allHypotheticalInferences, futureInference } =
    valueInference;

  const renderStandardArguments = () => {
    // This outer function will be called once when the component renders
    // The outer return happens once, returning the result of the map operation (an array of JSX elements)
    return expectedArguments.map((expectedArg, index) => {
      // This callback runs once for EACH element in expectedArguments
      // If expectedArguments has 2 elements, this callback executes twice

      // For each element in expectedArguments, get the corresponding stored inference
      const submittedArg = storedInference[index];

      // Create appropriate content based on whether submittedArg exists
      // This ternary operator decides which JSX to use based on the condition
      const argumentContent = submittedArg ? (
        // If submittedArg exists (true), use this JSX
        <SubmittedArgumentDisplay
          className="infIts-color submitted-argument"
          content={submittedArg}
        />
      ) : (
        // If submittedArg doesn't exist (false), use this JSX
        <SubmittedArgumentDisplay
          className="noArgumentYet-blinking"
          content="<Cliquez sur une inférence adéquate>"
        />
      );

      // This inner return occurs once per element in expectedArguments
      // It returns a single JSX element that will be added to the array created by map()
      // With 2 elements in expectedArguments, this inner return happens twice
      return (
        <li key={index} className="rule-modal-single-argument">
          <ExpectedArgumentDisplay content={expectedArg} />
          {argumentContent}
        </li>
      );
    });
    // After map() completes, it returns an array containing all the <li> elements
    // This array is what gets returned by the outer return statement
    // React will render all elements in this array in sequence
  };

  const renderRuleThatMayEndHypothesis = () => {
    const hypContent =
      allHypotheticalInferences.length >= 1 ? (
        // cas 1 : y'a bien une hypothèse, donc on l'affiche
        <SubmittedArgumentDisplay
          className="infIts-color submitted-argument"
          content={allHypotheticalInferences[0].itself}
        />
      ) : (
        // cas 2 : y'a pas encore d'hypothèse
        <SubmittedArgumentDisplay
          className="noArgumentYet-blinking"
          content="<Créez d'abord une hypothèse A>"
        />
      );
    const firstArgument = storedInference[0] ? (
      // cas 1 : y'a un inférence sélectionnée, donc on l'affiche
      <SubmittedArgumentDisplay
        className="infIts-color submitted-argument"
        content={storedInference[0]}
      />
    ) : (
      // cas 2 : y'a pas d'inférence sélectionnée
      <SubmittedArgumentDisplay
        className="noArgumentYet-blinking"
        content="<Cliquez sur une inférence B>"
      />
    );
    let liOfSecondArgument = "";
    if (expectedArguments[2]) {
      liOfSecondArgument = storedInference[1] ? (
        // cas 1 : y'a un inférence sélectionnée, donc on l'affiche
        <li className="rule-modal-single-argument">
          <ExpectedArgumentDisplay content={expectedArguments[2]} />
          <SubmittedArgumentDisplay
            className="infIts-color submitted-argument"
            content={storedInference[1]}
          />
        </li>
      ) : (
        // cas 2 : y'a pas d'inférence sélectionnée
        <li className="rule-modal-single-argument">
          <ExpectedArgumentDisplay content={expectedArguments[2]} />
          <SubmittedArgumentDisplay
            className="noArgumentYet-blinking"
            content={"<Cliquez sur une inférence ~B>"}
          />
        </li>
      );
    }

    return (
      <ul className="rule-modal-all-arguments">
        <li className="rule-modal-single-argument rule-modal-hypothetical-argument">
          <ExpectedArgumentDisplay content={expectedArguments[0]} />
          {hypContent}
        </li>
        <li className="rule-modal-single-argument">
          <ExpectedArgumentDisplay content={expectedArguments[1]} />
          {firstArgument}
        </li>
        {liOfSecondArgument}
      </ul>
    );
  };

  const renderArbitraryArgument = () => {
    const firstArgument = storedInference[0] ? (
      // cas 1 : y'a un inférence sélectionnée, donc on l'affiche
      <SubmittedArgumentDisplay
        className="infIts-color submitted-argument"
        content={storedInference[0]}
      />
    ) : (
      // cas 2 : y'a pas d'inférence sélectionnée
      <SubmittedArgumentDisplay
        className="noArgumentYet-blinking"
        content="<Cliquez sur une inférence A>"
      />
    );
    const secondArgument = storedInference[1] ? (
      // cas 1 : y'a un inférence sélectionnée, donc on l'affiche
      <SubmittedArgumentDisplay
        className="infIts-color submitted-argument"
        content={storedInference[1]}
      />
    ) : (
      // cas 2 : y'a pas d'inférence sélectionnée
      <SubmittedArgumentDisplay
        className="noArgumentYet-blinking"
        content="<Cliquez sur une inférence ~A>"
      />
    );

    const thirdArgument = futureInference ? (
      // cas 1 : y'a un au moins un caractère entré arbitrairement par l'utilisateurice, donc on affiche ce que l'utilisateurice a rentré
      <SubmittedArgumentDisplay
        className="infIts-color submitted-argument"
        content={futureInference}
      />
    ) : (
      // cas 2 : y'a pas encore de caractère entré par l'utilisateurice
      <SubmittedArgumentDisplay
        className="noArgumentYet-blinking"
        content="<Décidez arbitrairement de B>"
      />
    );
    return (
      <ul className="rule-modal-all-arguments">
        <li className="rule-modal-single-argument">
          <ExpectedArgumentDisplay content={expectedArguments[0]} />
          {firstArgument}
        </li>
        <li className="rule-modal-single-argument">
          <ExpectedArgumentDisplay content={expectedArguments[1]} />
          {secondArgument}
        </li>
        <li className="rule-modal-single-argument">
          <ExpectedArgumentDisplay content={"B"} />
          {thirdArgument}
        </li>
        <VirtualKeyboard
          onCharacterClick={(char) => valueInference.addToFutureInference(char)}
        />
      </ul>
    );
  };

  const renderByRuleType = () => {
    switch (ruleName) {
      case "⊃i":
        return renderRuleThatMayEndHypothesis();
      case "~i":
        return renderRuleThatMayEndHypothesis();
      case "∨i":
      case "ex falso":
        return renderArbitraryArgument();
      default:
        return renderStandardArguments();
    }
  };

  return renderByRuleType();
};

export default ShowExpectedArguments;

// class ShowExpectedArguments extends Component {
//   showKeyboard = () => {
//     let everyPossibleCharacter = [
//       ["~", "∧", "∨", "⊻", "⊃", "⊅", "≡", "↑", "↓"],
//       ["p", "q", "r", "s"],
//       ["(", ")"],
//     ];
//     let keyboard = [];
//     for (let i = 0; i < everyPossibleCharacter.length; i++) {
//       let subKeyboard = [];
//       for (let j = 0; j < everyPossibleCharacter[i].length; j++) {
//         subKeyboard.push(
//           <li
//             key={subKeyboard.length}
//             className="rule-modal-hypothesis-button-character selectable"
//             onClick={() => {
//               this.props.valueInference.addToFutureInference(
//                 everyPossibleCharacter[i][j]
//               );
//             }}
//           >
//             {everyPossibleCharacter[i][j]}
//           </li>
//         );
//       }
//       // (à faire) ici ce serait pas mal que ça mette aussi à jour la forecastInference
//       keyboard.push(<ul key={keyboard.length}>{subKeyboard}</ul>);
//     }
//     return <div className="rule-modal-all-button-hypothesis">{keyboard}</div>;
//   };

//   renderInstructionOrArgumentOfCurrentRule(
//     className,
//     instructionOrArgument,
//     otherInstructionOrArgument
//   ) {
//     let boolCursor = false;
//     if (instructionOrArgument[0] === "argument arbitraire") {
//       boolCursor = true;
//       instructionOrArgument = "";
//       otherInstructionOrArgument = instructionOrArgument[1];
//     }
//     return (
//       <div className={className}>
//         {instructionOrArgument}
//         {this.renderKeyboardBlinkingCursor(boolCursor)}
//         {otherInstructionOrArgument}
//       </div>
//     );
//   }

//   renderKeyboardBlinkingCursor(boolCursor) {
//     // utilisé les règles qui impliquent de pouvoir rentrer arbitrairement un contenu inférentiel (ex falso et ∨i)
//     if (boolCursor) {
//       return (
//         <ul className="typable-text">
//           {this.props.valueInference.futureInference}
//           <p className="blinking">_</p>
//         </ul>
//       );
//     }
//   }

//   render() {
//     let arrayExpectedArguments = [],
//       arrayEmptyArgument = [],
//       storedInference = this.props.valueInference.storedInference;
//     const ruleName = this.props.ruleName,
//       expectedArguments = this.props.expectedArguments,
//       allHypotheticalInferences =
//         this.props.valueInference.allHypotheticalInferences;

//     // déclaration des arguments attendus
//     let hypContent = this.renderInstructionOrArgumentOfCurrentRule(
//       "noArgumentYet-blinking",
//       "<Créez d'abord une hypothèse A, à l'aide de la règle \"hyp\">"
//     );
//     let firstArgument = this.renderInstructionOrArgumentOfCurrentRule(
//       "noArgumentYet-blinking",
//       "<Cliquez sur une inférence B au sein de l'hypothèse A>"
//     );
//     let secondArgument = this.renderInstructionOrArgumentOfCurrentRule(
//       "noArgumentYet-blinking",
//       "<Cliquez sur une inférence qui nie l'inférence B>"
//     );

//     if (
//       ruleName !== "⊃i" &&
//       ruleName !== "~i" &&
//       ruleName !== "∨i" &&
//       ruleName !== "ex falso"
//     ) {
//       // Toutes les règles, sauf les cas spécifiques comme en dessous
//       for (let i = 0; i < expectedArguments.length; i++) {
//         arrayEmptyArgument.push(
//           this.renderInstructionOrArgumentOfCurrentRule(
//             "noArgumentYet-blinking",
//             "<Cliquez sur une inférence adéquate>"
//           )
//         );
//         if (storedInference[i]) {
//           arrayEmptyArgument[i] = this.renderInstructionOrArgumentOfCurrentRule(
//             "infIts-color submitted-argument",
//             storedInference[i]
//           );
//         } else {
//           arrayEmptyArgument.push(
//             this.renderInstructionOrArgumentOfCurrentRule(
//               "noArgumentYet-blinking",
//               "<Cliquez sur une inférence adéquate>"
//             )
//           );
//         }
//         arrayExpectedArguments.push(
//           <li key={i} className="rule-modal-single-argument">
//             {this.renderInstructionOrArgumentOfCurrentRule(
//               "expected-argument",
//               expectedArguments[i]
//             )}
//             {this.renderInstructionOrArgumentOfCurrentRule(
//               "awaiting-instruction", // faut virer cette className, peut-être même virer ce
//               // arrayExpectedArguments.push()
//               arrayEmptyArgument[i]
//             )}
//           </li>
//         );
//       }
//     } else if (ruleName === "⊃i") {
//       // introduction du conditionnel
//       if (allHypotheticalInferences.length >= 1) {
//         hypContent = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           allHypotheticalInferences[0].itself
//         );
//       }
//       if (storedInference[0]) {
//         firstArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           storedInference[0]
//         );
//       }
//       arrayExpectedArguments.push(
//         <li
//           key={arrayExpectedArguments.length}
//           className="rule-modal-all-arguments"
//         >
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument rule-modal-hypothetical-argument",
//             expectedArguments[0],
//             hypContent
//           )}
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             expectedArguments[1],
//             firstArgument
//           )}
//         </li>
//       );
//     } else if (ruleName === "~i") {
//       if (allHypotheticalInferences.length >= 1) {
//         hypContent = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           allHypotheticalInferences[0].itself
//         );
//       }
//       if (storedInference[0]) {
//         firstArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           storedInference[0]
//         );
//       }
//       if (storedInference[1]) {
//         secondArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           storedInference[1]
//         );
//       }

//       arrayExpectedArguments.push(
//         <li
//           key={arrayExpectedArguments.length}
//           className="rule-modal-all-arguments"
//         >
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument rule-modal-hypothetical-argument",
//             expectedArguments[0],
//             hypContent
//           )}
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             expectedArguments[1],
//             firstArgument
//           )}
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             expectedArguments[2],
//             secondArgument
//           )}
//         </li>
//       );
//     } else if (ruleName === "∨i") {
//       let disjonctionArgument = this.renderInstructionOrArgumentOfCurrentRule(
//         "noArgumentYet-blinking",
//         "<Cliquez sur une inférence adéquate>"
//       );
//       let arbitraryArgument = this.renderInstructionOrArgumentOfCurrentRule(
//         "noArgumentYet-blinking",
//         "<Utilisez le clavier virtuel>"
//       );

//       if (storedInference[0]) {
//         disjonctionArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           storedInference[0]
//         );
//       }

//       if (this.props.valueInference.futureInference.length > 0) {
//         arbitraryArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           ["argument arbitraire", this.props.valueInference.futureInference]
//         );
//       }

//       const keyboard = this.showKeyboard();

//       arrayExpectedArguments.push(
//         <li
//           key={arrayExpectedArguments.length}
//           className="rule-modal-all-arguments"
//         >
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             expectedArguments[0],
//             disjonctionArgument
//           )}
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             "B",
//             arbitraryArgument
//           )}
//           {keyboard}
//         </li>
//       );
//       // } else if (ruleName === "∨e") {
//       //   arrayExpectedArguments.push(
//       //     <ShowDisjonctionEliminationArguments
//       //       key={arrayExpectedArguments.length}
//       //       whatToReturn="arguments"
//       //       expectedArguments={expectedArguments}
//       //       valueInference={this.props.valueInference}
//       //       valueRule={this.props.valueRule}
//       //       allHypotheticalInferences={allHypotheticalInferences}
//       //     />
//       //   );
//     } else if (ruleName === "ex falso") {
//       let exFalsoTrueArgument = this.renderInstructionOrArgumentOfCurrentRule(
//         "noArgumentYet-blinking",
//         "<Cliquez sur une inférence A>"
//       );
//       let exFalsoFalseArgument = this.renderInstructionOrArgumentOfCurrentRule(
//         "noArgumentYet-blinking",
//         "<Cliquez sur une inférence ~A>"
//       );
//       let arbitraryArgument = this.renderInstructionOrArgumentOfCurrentRule(
//         "noArgumentYet-blinking",
//         "<Utilisez le clavier virtuel>"
//       );

//       if (storedInference[0]) {
//         exFalsoTrueArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           storedInference[0]
//         );
//       }
//       if (storedInference[1]) {
//         exFalsoFalseArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           storedInference[1]
//         );
//       }

//       if (this.props.valueInference.futureInference.length > 0) {
//         arbitraryArgument = this.renderInstructionOrArgumentOfCurrentRule(
//           "infIts-color",
//           ["argument arbitraire", this.props.valueInference.futureInference]
//         );
//       }

//       const keyboard = this.showKeyboard();

//       arrayExpectedArguments.push(
//         <li
//           key={arrayExpectedArguments.length}
//           className="rule-modal-all-arguments"
//         >
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             expectedArguments[0],
//             exFalsoTrueArgument
//           )}
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             expectedArguments[1],
//             exFalsoFalseArgument
//           )}
//           {this.renderInstructionOrArgumentOfCurrentRule(
//             "rule-modal-single-argument",
//             "B",
//             arbitraryArgument
//           )}
//           {keyboard}
//         </li>
//       );
//     }

//     return arrayExpectedArguments;
//   }
// }

// export default ShowExpectedArguments;



// WEBPACK FOOTER //
// ./src/components/Modals and Popovers/Components/ShowExpectedArguments.js